import { InhalerUsage } from '@/jsonapi/CommonTypes'
import {
  buildDataPointsByDay,
  buildDataPointsByDayWithRawData,
  fillDate,
} from '@/components/patient-stats/common/utils'
import {
  buildAxis,
  buildMarkLine,
  getMarkLineRange,
} from '@/components/patient-stats/graphs/detailed-reports/options/option-builders'
import moment from 'moment-timezone'
import { CHART_DATE_FORMAT } from '@/components/patient-stats/constants'
import { removeAbbr } from '@/utils/time'
import { withSingularOrPluralLabel } from '@/utils/labels'
import XAxis = echarts.EChartOption.XAxis
import YAxis = echarts.EChartOption.YAxis
import DataObject = echarts.EChartOption.SeriesLine.DataObject

export function buildNightTimeInhalerUsageOption(options: {
  resultStartTimestamp: number
  resultEndTimestamp: number
  timezone: string
  colors: Record<any, any> // vuetify theme
  nightInhalerUsages: InhalerUsage[]
}): echarts.EChartOption<echarts.EChartOption.SeriesBar> {
  const { timezone, resultStartTimestamp, resultEndTimestamp, colors, nightInhalerUsages } = options
  const xAxisLabels: string[] = [...fillDate(timezone, resultStartTimestamp, resultEndTimestamp)].map(timestamp =>
    String(timestamp)
  )

  const nightInhalerDataPointsWithName = nightInhalerUsages
    .map(usage => {
      return usage.dataPoints.map(dataPoint => ({
        ...dataPoint,
        name: usage.name,
      }))
    })
    .flat(Infinity)
  const nighInhalerDataPointsByDay = buildDataPointsByDayWithRawData(nightInhalerDataPointsWithName, timezone)
  const seriesData = Object.entries(nighInhalerDataPointsByDay).sort((a, b) => Number(a[0]) - Number(b[0]))

  console.log(seriesData)
  const maxYValue = Math.max(
    8,
    ...Object.values(buildDataPointsByDay(nightInhalerUsages.map(usage => usage.dataPoints).flat(Infinity), timezone))
  )

  const lines = getMarkLineRange({
    maxYValue: maxYValue,
    minStep: 2,
    minEnd: 8,
    numOfSeparate: 4,
  })

  return {
    grid: [{ x: '2.5%', y: '5%', width: '95%', height: '95%', containLabel: true } as echarts.EChartOption.Grid],
    xAxis: buildAxis({
      data: xAxisLabels,
      axisType: 'x',
      formatter: param => {
        return moment(parseInt(param, 10))
          .tz(timezone)
          .format(CHART_DATE_FORMAT.DATE)
      },
    }) as XAxis,
    yAxis: buildAxis({
      axisType: 'y',
      maxValue: maxYValue,
    }) as YAxis,
    series: [
      {
        type: 'bar',
        silent: true,
        stack: 'data',
        // @ts-ignore
        markLine: buildMarkLine(lines),
      } as DataObject | any,
      {
        type: 'bar',
        silent: true,
        stack: 'data',
        label: { show: false },
        itemStyle: {
          barBorderRadius: [4, 4, 0, 0],
        },
        // @ts-ignore
        data: seriesData.map(d => ({
          value: [d[0], d[1].value, d[1]],
          itemStyle: {
            color: d[1].value >= 4 ? colors.burgundy : colors.teal,
          },
        })) as DataObject | any,
      },
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: params => {
        if (!Array.isArray(params)) {
          return null
        }

        const title =
          moment(parseInt(params[0].axisValue as string, 10))
            .tz(timezone)
            .format(removeAbbr(timezone, CHART_DATE_FORMAT.DAY_ABBR)) + '<br>'

        const data = params[0].value[2]
        if (data.value === 0) {
          return title + 'No usage'
        }

        console.log(data)
        return (
          title +
          data.rawData
            .map(raw => {
              return raw.value === 0 ? '' : `${raw.name} ${withSingularOrPluralLabel(raw.value, 'time', 'times')}<br>`
            })
            .join('')
        )
      },
    },
  }
}
